<template>
  <div class="modern-form message-logs-wrapper">
    <v-container>
      <v-row noGutters class="mb-3">
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="search"
            label="Search"
            outlined
            dense
            hide-details
            @keyup.enter="handleSearch"
            class="search-input" />
        </v-col>
      </v-row>

      <div class="text-center" v-if="loading">
        <v-progress-circular
          indeterminate
          color="primary"
          class="loading-spinner" />
      </div>

      <div v-else class="messages-table">
        <div v-if="emails.length === 0" class="no-results">
          <v-icon class="no-results-icon">mdi-email-off</v-icon>
          <p class="no-results-text">No emails found</p>
        </div>
        <template v-else>
        <div class="table-header">
          <div class="header-cell to-col sortable" @click="handleSort('to')">
            To
            <v-icon small v-if="sortBy === 'to'">{{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
          </div>
          <div class="header-cell subject-col sortable" @click="handleSort('subject')">
            Subject
            <v-icon small v-if="sortBy === 'subject'">{{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
          </div>
          <div class="header-cell date-col sortable" @click="handleSort('dateSent')">
            Date/Time
            <v-icon small v-if="sortBy === 'dateSent'">{{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
          </div>
        </div>
        <div class="table-body">
          <div
            v-for="item in emails"
            :key="item.id"
            class="message-row">
            <div class="message-cell to-col">
              {{ item.to }}
            </div>
            <div class="message-cell subject-col">
              {{ item.subject }}
            </div>
            <div class="message-cell date-col">
              {{ formatDate(item.dateSent) }} {{ formatTime(item.dateSent) }}
            </div>
          </div>
        </div>
        </template>
      </div>

      <div v-if="!loading" class="pagination-container">
        <div class="pagination-size">
          <v-select
            v-model="pageSize"
            :items="pageSizes"
            label="Items"
            dense
            outlined
            hide-details
            @change="handlePageSizeChange" />
        </div>
        <v-pagination
          v-model="page"
          :length="totalPages"
          total-visible="5"
          next-icon="mdi-menu-right"
          prev-icon="mdi-menu-left"
          class="custom-pagination"
          @input="handlePageChange" />
      </div>
    </v-container>
  </div>
</template>

<script>
import DataService from '../../../_services/DataService';
import Utils from '../../../src/utils.js';

export default {
  name: 'Email',
  data() {
    return {
      search: '',
      loading: true,
      emails: [],
      page: 1,
      totalPages: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100, 200],
      sortBy: 'dateSent',
      sortDesc: true,
      formatDate: Utils.formatDate,
      formatTime: Utils.formatTime,
    };
  },
  methods: {
    handleSort(column) {
      if (this.sortBy === column) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = column;
        this.sortDesc = false;
      }
      this.fetchEmails();
    },
    async fetchEmails() {
      this.loading = true;
      const params = {
        orderkey: this.sortBy,
        orderkeyVal: this.sortDesc ? 'DESC' : 'ASC',
        page: this.page - 1,
        size: this.pageSize,
        search: this.search,
      };

      const response = await DataService.getEmails(params);
      if (response.status === 200) {
        this.emails = response.data.rows;
        this.totalPages = Math.ceil(response.data.count / this.pageSize);
      }
      this.loading = false;
    },
    async handlePageChange(value) {
      this.page = value;
      await this.fetchEmails();
    },
    async handlePageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      await this.fetchEmails();
    },
    async handleSearch() {
      this.page = 1;
      await this.fetchEmails();
    },
  },
  async mounted() {
    await this.fetchEmails();
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';

$table-header-bg-color: #f8fafc;
$table-header-text-color: #374151;
$table-border-color: #e2e8f0;
$message-row-hover-bg: #f8fafc;
$message-row-border-color: #f1f5f9;
$message-cell-text-color: #374151;
$pagination-border-color: #e2e8f0;
$pagination-active-bg: #133b54;
$sort-icon-color: #6b7280;
$header-hover-bg: #edf2f7;
$no-results-icon-color: #cbd5e0;
$no-results-text-color: #718096;

.messages-table {
  .table-header {
    display: grid;
    grid-template-columns: 2fr 2fr 1.5fr;
    background: $table-header-bg-color;
    border-bottom: 1px solid $table-border-color;
    
    @media (max-width: $breakpoint-mobile) {
      display: none;
    }
    
    .header-cell {
      padding: 16px 24px;
      font-size: 14px;
      font-weight: 600;
      color: $table-header-text-color;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      
      &.sortable {
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        gap: 4px;
        
        &:hover {
          background-color: $header-hover-bg;
        }
        
        .v-icon {
          color: $sort-icon-color;
        }
      }
    }
  }
  
  .table-body {
    .message-row {
      display: grid;
      grid-template-columns: 2fr 2fr 1.5fr;
      border-bottom: 1px solid $message-row-border-color;
      transition: background-color $transition-fast ease;
      
      @media (max-width: $breakpoint-mobile) {
        grid-template-columns: 1fr;
        gap: 8px;
        padding: 16px;
      }
      
      &:hover {
        background-color: $message-row-hover-bg;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      .message-cell {
        padding: 16px 24px;
        font-size: 14px;
        color: $message-cell-text-color;
        display: flex;
        align-items: center;
        
        @media (max-width: $breakpoint-mobile) {
          padding: 8px 0;
          font-size: 15px;
          line-height: 1.4;
          flex-wrap: wrap;
          
          &::before {
            font-weight: 600;
            color: $table-header-text-color;
            margin-right: 8px;
            flex-shrink: 0;
          }
        }
        
        &.to-col {
          @media (max-width: $breakpoint-mobile) {
            &::before {
              content: 'To:';
            }
          }
        }
        
        &.subject-col {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          
          @media (max-width: $breakpoint-mobile) {
            white-space: normal;
            word-break: break-word;
            overflow-wrap: break-word;
            
            &::before {
              content: 'Subject:';
            }
          }
        }
        
        &.date-col {
          @media (max-width: $breakpoint-mobile) {
            font-size: 14px;
            
            &::before {
              content: 'Date:';
            }
          }
        }
      }
    }
  }
}

  .pagination-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;
    padding: 12px 0;

    .pagination-size {
      width: 90px;
    }

    .custom-pagination {
      ::v-deep .v-pagination {
        &__item {
          box-shadow: none;
          border: 1px solid $pagination-border-color;

          &--active {
            background-color: $pagination-active-bg !important;
            color: white !important;
            border-color: $pagination-active-bg !important;
          }
        }

        &__more {
          width: 22px;
          margin-left: 0;
          margin-right: 0;
        }

        &__navigation {
          box-shadow: none;
          border: 1px solid $pagination-border-color;
        }
      }
    }
  }

.loading-spinner {
  margin: 40px auto;
}

.no-results {
  text-align: center;
  padding: 80px 24px;
  
  .no-results-icon {
    font-size: 64px;
    color: $no-results-icon-color;
    margin-bottom: 16px;
  }
  
  .no-results-text {
    color: $no-results-text-color;
    margin: 0;
    font-size: 16px;
  }
}

// Wrapper styling to prevent pagination overflow
// When there are many pages, the pagination can extend beyond the container width
.message-logs-wrapper {
  .v-container {
    padding-bottom: 32px;
    max-width: 100%;
    overflow-x: hidden;
  }
  
  .pagination-container {
    max-width: 100%;
    overflow-x: auto;
  }
}
</style>
